// Client-side PostHog for the landing page, pageviews, autocapture (clicks),
// web-vitals, and custom conversion events. Uses the shared Unacast/Pine59
// PostHog project so the full journey (landing -> signup -> product) lives in
// one funnel; events are tagged `site: 'landing-page'` to filter them apart
// from the product app. Mirrors unacat-console's client setup.
//
// TRACKING POSTURE (2026-07, legal-approved): analytics runs by default with
// PostHog's default persistence (cookies + localStorage), and there is NO
// consent banner. Counsel confirmed that for our first-party, EU-hosted,
// internal-use analytics a cookie opt-in gate is not required; because there is
// no opt-in there is likewise no opt-out. The privacy policy discloses the
// analytics cookies and the legitimate-interest basis. To go cookieless again,
// set `persistence: "memory"` (git history has the full banner implementation).
//
// Config is baked at build time via Vite (see vite.config.ts) with the real
// project values as defaults, overridable per-env:
//   VITE_POSTHOG_ENABLED     "true" to turn on (default: on in production)
//   VITE_POSTHOG_KEY         project API key (public phc_ key)
//   VITE_POSTHOG_PROXY_PATH  same-origin relay path (app/routes/ph-relay-uc.$.tsx),
//                            same convention as unacat-console
//   VITE_POSTHOG_UI_HOST     https://eu.posthog.com (so links point back to PH)

let initialized = false
let initInFlight: Promise<void> | null = null
let posthog: any

export function initPosthog(): void {
  if (typeof window === "undefined" || initialized || initInFlight) return
  if (import.meta.env.VITE_POSTHOG_ENABLED !== "true") return

  const apiKey = import.meta.env.VITE_POSTHOG_KEY as string | undefined
  const apiHost = import.meta.env.VITE_POSTHOG_PROXY_PATH as string | undefined
  const uiHost = import.meta.env.VITE_POSTHOG_UI_HOST as string | undefined
  if (!apiKey || !apiHost || !uiHost) {
    if (!apiKey) console.warn("VITE_POSTHOG_KEY not set; PostHog disabled.")
    return
  }

  initInFlight = import("posthog-js")
    .then(({ default: ph }) => {
      posthog = ph
      ph.init(apiKey, {
        api_host: apiHost,
        ui_host: uiHost,
        defaults: "2026-05-30",
        person_profiles: "identified_only",
        capture_pageview: true,
        capture_performance: true, // web vitals
        autocapture: true,
        // No surveys on the landing page: skips the 34 KiB surveys.js bundle
        // posthog-js otherwise lazy-loads on every visit.
        disable_surveys: true,
        // Show form input text in session recordings (posthog-js masks all
        // inputs by default). Mirrors unacat-console; passwords stay masked
        // (none exist on this site today, kept as a guard).
        session_recording: {
          maskAllInputs: false,
          maskInputFn: (text: string, element?: HTMLElement) => {
            if (element?.getAttribute?.("type") === "password") {
              return "•".repeat(text.length)
            }
            return text
          },
        },
        // Default persistence (localStorage + cookie), enables persistent,
        // cross-session visitor identity. No consent banner (see note above).
      })
      // Tag every event from this site so it can be filtered apart from the
      // product app in shared dashboards/insights.
      ph.register({ site: "landing-page" })
      initialized = true
    })
    .catch(() => {})
    .finally(() => {
      initInFlight = null
    })
}

/** Fire a custom event (e.g. a CTA click). Safe before init, no-ops silently. */
export function capture(event: string, props?: Record<string, unknown>): void {
  if (typeof window === "undefined") return
  try {
    if (!initialized) initPosthog()
    posthog?.capture?.(event, props)
  } catch {
    // best-effort only
  }
}

export { posthog }
